import React from "react";
import { supabase } from "@/integrations/supabase/client";
import { consumeFreeQuota, FREE_QUOTA_MESSAGE } from "@/lib/plan/freeTier";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Copy, Loader2, Share2, Trash2, Eye, Pencil } from "lucide-react";

export interface ShareTarget {
  projectId: string;
  documentType: string;
  documentId: string;
}

interface ShareRow {
  id: string;
  recipient_email: string;
  recipient_name: string | null;
  permission: "view" | "edit";
  expires_at: string | null;
  revoked_at: string | null;
  open_count: number | null;
  edit_count: number | null;
  last_opened_at: string | null;
}

export const ShareExternallyDialog: React.FC<{ target: ShareTarget }> = ({ target }) => {
  const [open, setOpen] = React.useState(false);
  const [emails, setEmails] = React.useState("");
  const [permission, setPermission] = React.useState<"view" | "edit">("view");
  const [expiresInDays, setExpiresInDays] = React.useState("30");
  const [message, setMessage] = React.useState("");
  const [sending, setSending] = React.useState(false);
  const [shares, setShares] = React.useState<ShareRow[]>([]);
  const [links, setLinks] = React.useState<{ email: string; url: string }[]>([]);

  const loadShares = React.useCallback(async () => {
    const { data } = await supabase
      .from("document_shares")
      .select(
        "id, recipient_email, recipient_name, permission, expires_at, revoked_at, open_count, edit_count, last_opened_at",
      )
      .eq("document_type", target.documentType)
      .eq("document_id", target.documentId)
      .order("created_at", { ascending: false });
    setShares((data as ShareRow[]) ?? []);
  }, [target.documentType, target.documentId]);

  React.useEffect(() => {
    if (open) void loadShares();
  }, [open, loadShares]);

  const handleSend = async () => {
    const recipients = emails
      .split(/[,;\s]+/)
      .map((e) => e.trim())
      .filter(Boolean);

    if (recipients.length === 0) {
      toast.error("Ange minst en e-postadress");
      return;
    }

    setSending(true);
    const quota = await consumeFreeQuota("link");
    if (!quota.allowed) {
      setSending(false);
      toast.error("Kvoten för externa länkar är slut", {
        description: FREE_QUOTA_MESSAGE.link,
      });
      return;
    }
    const { data, error } = await supabase.functions.invoke("create-document-share", {

      body: {
        projectId: target.projectId,
        documentType: target.documentType,
        documentId: target.documentId,
        recipients,
        permission,
        expiresInDays: Number(expiresInDays) || 30,
        message: message.trim() || undefined,
        appOrigin: window.location.origin,
      },
    });
    setSending(false);

    if (error || (data as any)?.error) {
      toast.error((data as any)?.error ?? "Kunde inte skapa delningen");
      return;
    }

    const result = data as { shares: { email: string; url: string }[]; emailStatus: string };
    setLinks(result.shares);
    setEmails("");
    setMessage("");
    void loadShares();

    if (result.emailStatus === "sent") {
      toast.success("Inbjudan skickad med e-post");
    } else {
      toast.success("Länkar skapade – kopiera och skicka dem själv");
    }
  };

  const revoke = async (id: string) => {
    const { error } = await supabase
      .from("document_shares")
      .update({ revoked_at: new Date().toISOString() })
      .eq("id", id);
    if (error) {
      toast.error("Kunde inte återkalla delningen");
      return;
    }
    toast.success("Delningen är återkallad");
    void loadShares();
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="gap-2">
          <Share2 className="h-4 w-4" />
          Dela externt
        </Button>
      </DialogTrigger>
      <DialogContent className="max-h-[85vh] max-w-lg overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Dela dokumentet externt</DialogTitle>
          <DialogDescription>
            Mottagaren öppnar dokumentet via en säker länk – utan konto i systemet. Ändringar sparas
            direkt i projektet och loggas.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>E-postadresser</Label>
            <Textarea
              rows={2}
              placeholder="namn@foretag.se, annan@foretag.se"
              value={emails}
              onChange={(e) => setEmails(e.target.value)}
            />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label>Behörighet</Label>
              <Select value={permission} onValueChange={(v) => setPermission(v as "view" | "edit")}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="view">Endast läsa</SelectItem>
                  <SelectItem value="edit">Läsa och redigera</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Giltig i</Label>
              <Select value={expiresInDays} onValueChange={setExpiresInDays}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="7">7 dagar</SelectItem>
                  <SelectItem value="30">30 dagar</SelectItem>
                  <SelectItem value="90">90 dagar</SelectItem>
                  <SelectItem value="365">1 år</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Meddelande (valfritt)</Label>
            <Input
              placeholder="T.ex. Vänligen granska avsnitt 5"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
            />
          </div>

          <Button className="w-full gap-2" onClick={handleSend} disabled={sending}>
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Share2 className="h-4 w-4" />}
            Skapa och skicka länk
          </Button>

          {links.length > 0 && (
            <div className="space-y-2 rounded-lg border border-border/60 bg-muted/30 p-3">
              <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Länkar
              </p>
              {links.map((link) => (
                <div key={link.url} className="flex items-center gap-2">
                  <Input readOnly value={link.url} className="h-8 text-xs" />
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-8 w-8 shrink-0"
                    onClick={() => {
                      void navigator.clipboard.writeText(link.url);
                      toast.success("Länk kopierad");
                    }}
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}

          {shares.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Aktiva delningar
              </p>
              {shares.map((share) => (
                <div
                  key={share.id}
                  className="flex items-center justify-between gap-3 rounded-lg border border-border/60 p-3"
                >
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium">
                      {share.recipient_name ?? share.recipient_email}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {share.revoked_at
                        ? "Återkallad"
                        : `${share.open_count ?? 0} öppningar · ${share.edit_count ?? 0} ändringar`}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary" className="gap-1">
                      {share.permission === "edit" ? (
                        <Pencil className="h-3 w-3" />
                      ) : (
                        <Eye className="h-3 w-3" />
                      )}
                      {share.permission === "edit" ? "Redigera" : "Läsa"}
                    </Badge>
                    {!share.revoked_at && (
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-8 w-8 text-destructive"
                        onClick={() => revoke(share.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ShareExternallyDialog;
